import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Award, LogOut, Package, Sparkles, MessageSquare, ArrowLeft, Loader2, Truck, Coins, ChevronRight, UserCog, Download, Trash2, Save, ShieldCheck, Heart, MapPin, Megaphone, Copy, Check, Mail } from "lucide-react";
import { confirmElite } from "../lib/confirmElite";
import { bgWithImageStyle } from "../utils/themeBg";
import { zoneCapsForProvince } from "../utils/shipping";
import { comuneNameForCap } from "../utils/comuni";
import { useTheme } from "../context/ThemeContext";
import ProductCard from "../components/leovera/ProductCard";
import { useWishlist } from "../context/WishlistContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const StatCard = ({ label, value, hint, icon: Icon, testId }) => (
  <div data-testid={testId} className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">{label}</span>
      {Icon && <Icon className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />}
    </div>
    <p className="font-[Outfit] text-white text-3xl tracking-tight font-light">{value}</p>
    {hint && <p className="mt-2 text-[11px] text-[#94A3B8]">{hint}</p>}
  </div>
);

const Account = () => {
  const { user, loading, logout, userApi } = useAuth();
  const { settings } = useTheme();
  const { ids: wishlistIds } = useWishlist();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loyalty, setLoyalty] = useState({ balance: 0, total_earned: 0, transactions: [], ratio: "2 punti ogni 10 € di spesa" });
  const [stats, setStats] = useState({ orders_total: 0, orders_paid: 0, total_spent: 0, loyalty_points: 0, movements_total: 0 });
  const [tab, setTab] = useState("orders"); // orders | deliveries | loyalty | wishlist | profile
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [profile, setProfile] = useState({ name: "", codice_sdi: "", pec_fatturazione: "" });
  const [profileMsg, setProfileMsg] = useState("");
  const [profileBusy, setProfileBusy] = useState(false);
  const isPr = (user?.role_tag || "cliente") === "pr";
  const [prStats, setPrStats] = useState(null);
  const [prLoading, setPrLoading] = useState(false);
  const [prCopied, setPrCopied] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const handleResendVerification = async () => {
    setResendBusy(true);
    try {
      await userApi.post("/auth/resend-verification");
      setResendSent(true);
      toast.success("Email di conferma inviata di nuovo: controlla la posta.", { id: "resend-verify", position: "top-center" });
    } catch {
      toast.error("Non è stato possibile inviare l'email, riprova tra poco.", { id: "resend-verify", position: "top-center" });
    } finally {
      setResendBusy(false);
    }
  };

  useEffect(() => {
    if (!user || !isPr) return;
    setPrLoading(true);
    userApi.get("/pr/me").then(({ data }) => setPrStats(data)).catch(() => setPrStats(null)).finally(() => setPrLoading(false));
  }, [user, isPr, userApi]);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        codice_sdi: user.codice_sdi || "",
        pec_fatturazione: user.pec_fatturazione || "",
      });
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [o, m, l, s] = await Promise.all([
          userApi.get("/auth/orders"),
          userApi.get("/auth/movements"),
          userApi.get("/auth/loyalty"),
          userApi.get("/auth/stats"),
        ]);
        setOrders(o.data || []);
        setMovements(m.data || []);
        setLoyalty(l.data || loyalty);
        setStats(s.data || stats);
      } catch { /* empty */ }
      finally { setLoadingOrders(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userApi]);

  useEffect(() => {
    if (!user) return;
    userApi.get("/account/wishlist")
      .then(({ data }) => setWishlistItems(data || []))
      .catch(() => {})
      .finally(() => setWishlistLoading(false));
  }, [user, userApi]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--lv-bg,#060A14)] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#C0C0C0] animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: "/account" }} replace />;

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  const saveProfile = async () => {
    setProfileBusy(true); setProfileMsg("");
    try {
      await userApi.patch("/auth/profile", profile);
      setProfileMsg("Profilo aggiornato ✓");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setProfileMsg(typeof d === "string" ? d : "Errore nel salvataggio.");
    } finally { setProfileBusy(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await userApi.get("/account/export-data");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "leovera_miei_dati.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch { setProfileMsg("Errore nell'esportazione dei dati."); }
  };

  const deleteAccount = async () => {
    const ok = await confirmElite(
      "Eliminare definitivamente il tuo account? Perderai punti fedeltà, storico ordini e recensioni. Questa azione è irreversibile (GDPR art. 17)."
    );
    if (!ok) return;
    try {
      await userApi.delete("/account/delete");
      logout();
      navigate("/", { replace: true });
    } catch { setProfileMsg("Errore nell'eliminazione dell'account."); }
  };

  // Loyalty tiers: Bronze 0-99, Silver 100-499, Gold 500+
  const points = user.loyalty_points || 0;
  let tier = "Bronze";
  let nextThreshold = 100;
  if (points >= 500) { tier = "Gold"; nextThreshold = null; }
  else if (points >= 100) { tier = "Silver"; nextThreshold = 500; }

  return (
    <div className="min-h-screen text-white" style={bgWithImageStyle()} data-testid="account-page">
      <header className="border-b border-white/10 backdrop-blur-md sticky top-0 z-20" style={{ backgroundColor: "color-mix(in srgb, var(--lv-bg,#060A14) 95%, transparent)" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="account-home-link" className="inline-flex items-center gap-3">
            <img src={LOGO_URL} alt="LEOVERA" className="h-9 w-auto bg-white rounded-md p-1" />
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#94A3B8]">Area Membri</span>
          </Link>
          <div className="flex items-center gap-2">
            <span data-testid="account-header-loyalty"
                  className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#C0C0C0]/30 bg-[var(--lv-surface,#0A1121)] text-[10px] tracking-[0.24em] uppercase text-[#E5E4E2]">
              <Award className="w-3 h-3 text-amber-300" />
              {points} pt · {tier}
            </span>
            <button onClick={handleLogout} data-testid="account-logout-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[10px] tracking-[0.28em] uppercase text-[#E5E4E2] hover:bg-red-500/10 hover:text-red-300">
              <LogOut className="w-3.5 h-3.5" />
              Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <section className="mb-10">
          <span className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0]">Bentornato</span>
          <h1 className="mt-2 font-[Outfit] text-white text-3xl sm:text-5xl font-light tracking-tight">
            {user.name || "Atleta"}
          </h1>
          <p className="mt-2 text-[#94A3B8] text-sm">{user.email}</p>
        </section>

        {user?.email_verified === false && (
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3.5 rounded-sm bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[12px]" data-testid="account-email-unverified-notice">
            <span className="flex items-center gap-2">
              <Mail className="w-4 h-4 shrink-0" />
              Conferma la tua email per collegare automaticamente al tuo account eventuali ordini fatti in precedenza come ospite.
            </span>
            <button type="button" onClick={handleResendVerification} disabled={resendBusy || resendSent}
                    data-testid="account-resend-verification-btn"
                    className="shrink-0 underline underline-offset-2 hover:text-white font-medium disabled:opacity-60 disabled:no-underline">
              {resendBusy ? "Invio..." : resendSent ? "Email inviata" : "Invia di nuovo l'email di conferma"}
            </button>
          </div>
        )}

        {/* Stat grid */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <StatCard label="Ordini" value={stats.orders_total || orders.length} icon={Package} testId="stat-orders" />
          <StatCard label="Speso totale" value={`€${Number(stats.total_spent || 0).toFixed(2)}`} icon={Coins} testId="stat-spent" />
          <StatCard label="Punti Cashback" value={loyalty.balance} icon={Award} testId="stat-points" />
          <StatCard label="Consegne" value={stats.movements_total || 0} icon={Truck} testId="stat-deliveries" />
        </section>

        {user?.renato_whatsapp_optin && (
          <a href="https://wa.me/393277620114?text=Ciao%20Leo!%20Mi%20sono%20registrato%20su%20LEOVERA%20Nutrition"
             target="_blank" rel="noopener noreferrer" data-testid="account-renato-whatsapp-btn"
             className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-[12px] font-semibold shadow-lg transition-transform hover:scale-[1.02]"
             style={{ backgroundColor: "#25D366" }}>
            💬 Aggiungi Leo su WhatsApp
          </a>
        )}

        {/* Tab navigation */}
        <div className="flex flex-wrap gap-2 mb-5">
          {[
            ["orders",     "Cronologia ordini"],
            ["deliveries", "Consegne"],
            ["loyalty",    "Cashback Elite"],
            ["wishlist",   "Preferiti"],
            ...(isPr ? [["pr", "Area PR"]] : []),
            ["profile",    "Profilo & Privacy"],
          ].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`account-tab-${v}`}
                    className={`px-4 py-2 rounded-sm text-[10px] tracking-[0.24em] uppercase border transition-colors ${
                      tab === v ? "bg-[#C0C0C0]/15 border-[#C0C0C0]/40 text-white"
                                : "border-white/10 text-[#94A3B8] hover:text-white hover:border-white/30"
                    }`}>
              {l}
            </button>
          ))}
        </div>

        {/* TAB: ORDERS */}
        {tab === "orders" && (
          <section className="mb-10" data-testid="tab-orders">
            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md overflow-hidden">
              {loadingOrders ? (
                <div className="p-6 text-center text-[#94A3B8] text-sm">Caricamento…</div>
              ) : orders.length === 0 ? (
                <div data-testid="account-orders-empty" className="p-8 text-center">
                  <Package className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-[#E5E4E2] text-sm">Nessun ordine ancora.</p>
                  <p className="text-[#94A3B8] text-[11px] mt-1">Il tuo primo acquisto ti farà guadagnare i primi punti fedeltà.</p>
                  <Link to="/" className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] hover:text-white">
                    Vai al catalogo
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {orders.map((o) => {
                    const prov = (o.shipping_province || "").trim().toUpperCase();
                    const orderCap = (o.shipping_postal_code || "").trim();
                    const caps = zoneCapsForProvince(prov, settings);
                    const inComune = caps.length > 0 && caps.includes(orderCap);
                    const comuneName = inComune ? comuneNameForCap(orderCap) : "";
                    return (
                      <div key={o.id} data-testid={`account-order-${o.id}`} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-[Outfit] text-white text-sm tracking-tight">{o.order_number}</p>
                          <p className="text-[11px] text-[#94A3B8] mt-1">
                            {(o.created_at || "").slice(0, 10)} · {o.items?.length || 0} articoli · {o.fulfillment_status || o.status}
                          </p>
                          {o.shipping_address && (
                            <p className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`account-order-address-${o.id}`}>
                              <MapPin className="w-3 h-3 shrink-0" />
                              <span className="truncate">{o.shipping_address}, {o.shipping_city} ({prov}) {o.shipping_postal_code}</span>
                              {inComune && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[9px] tracking-[0.14em] uppercase shrink-0"
                                      data-testid={`account-order-comune-badge-${o.id}`}>
                                  {comuneName}
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                        <span className="font-[Outfit] text-[#E5E4E2] text-lg shrink-0">€{Number(o.total || o.amount_total || 0).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        )}

        {/* TAB: DELIVERIES */}
        {tab === "deliveries" && (
          <section className="mb-10" data-testid="tab-deliveries">
            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md overflow-hidden">
              {movements.length === 0 ? (
                <div className="p-8 text-center">
                  <Truck className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-[#E5E4E2] text-sm">Nessuna consegna ancora.</p>
                  <p className="text-[#94A3B8] text-[11px] mt-1">Le tue consegne appariranno qui quando saranno completate.</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {movements.map((m) => (
                    <div key={m.id} data-testid={`account-mov-${m.id}`} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="font-[Outfit] text-white text-sm tracking-tight">{m.product_name}</p>
                        <p className="text-[11px] text-[#94A3B8] mt-1">
                          {(m.created_at || "").slice(0, 16).replace("T"," ")} · Qtà {m.qty}
                          {m.party ? ` · ${m.party}` : ""}
                        </p>
                      </div>
                      <span className="inline-flex items-center gap-1.5 text-[9px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                        Consegnato
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* TAB: WISHLIST */}
        {tab === "wishlist" && (
          <section className="mb-10" data-testid="tab-wishlist">
            {wishlistLoading ? (
              <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6 text-center text-[#94A3B8] text-sm">
                Caricamento…
              </div>
            ) : wishlistItems.filter((p) => wishlistIds.includes(p.id)).length === 0 ? (
              <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-8 text-center" data-testid="account-wishlist-empty">
                <Heart className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.2} />
                <p className="text-[#E5E4E2] text-sm">Nessun preferito salvato.</p>
                <p className="text-[#94A3B8] text-[11px] mt-1">Tocca il cuore su un prodotto per salvarlo qui.</p>
                <Link to="/" className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] hover:text-white">
                  Vai al catalogo
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-5">
                {wishlistItems.filter((p) => wishlistIds.includes(p.id)).map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </section>
        )}


        {/* TAB: LOYALTY (Cashback Elite) */}
        {tab === "loyalty" && (
          <section className="mb-10 space-y-5" data-testid="tab-loyalty">
            <div className="relative overflow-hidden rounded-md border border-[#C0C0C0]/30 bg-gradient-to-br from-[#0F1A2E] via-[var(--lv-surface,#0A1121)] to-[var(--lv-bg,#060A14)] p-7">
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-[#C0C0C0]/10 blur-3xl" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.36em] uppercase text-[#C0C0C0]">
                  <Award className="w-3.5 h-3.5" /> Cashback Elite
                </span>
                <p className="mt-3 font-[Outfit] text-white text-5xl sm:text-6xl tracking-tight font-light">
                  <span data-testid="loyalty-balance">{loyalty.balance}</span>
                  <span className="text-[#94A3B8] text-2xl ml-3">punti</span>
                </p>
                <p className="mt-3 text-[12px] text-[#94A3B8] max-w-lg">
                  {loyalty.ratio}. I punti vengono accreditati automaticamente <strong className="text-white">al momento della consegna</strong> dell'ordine.
                  Puoi riscattarli contattando Leo per ottenere sconti sui prossimi acquisti.
                </p>
                <p className="mt-2 text-[11px] text-[#94A3B8]">
                  Totale guadagnato dall'inizio: <span className="text-white">{loyalty.total_earned}</span> punti
                </p>
              </div>
            </div>

            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md overflow-hidden">
              <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-white text-[13px] font-medium tracking-tight">Storico movimenti punti</h3>
                <span className="text-[10px] text-[#94A3B8]">{loyalty.transactions.length} transazioni</span>
              </div>
              {loyalty.transactions.length === 0 ? (
                <div className="p-8 text-center text-[#94A3B8] text-sm" data-testid="loyalty-empty">
                  Nessun punto accumulato ancora. I punti compaiono qui quando un ordine viene consegnato.
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {loyalty.transactions.map((t) => (
                    <div key={t.id} data-testid={`loyalty-tx-${t.id}`} className="p-4 sm:p-5 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-[#E5E4E2] text-sm">{t.product_name || "Consegna"}</p>
                        <p className="text-[11px] text-[#94A3B8] mt-0.5">
                          {(t.created_at || "").slice(0, 16).replace("T"," ")}
                          {t.value_eur ? ` · €${Number(t.value_eur).toFixed(2)}` : ""}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="font-[Outfit] text-emerald-300 text-lg">+{t.points}</span>
                        <p className="text-[10px] text-[#94A3B8] mt-0.5">saldo: {t.balance_after}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* TAB: PR SELF-SERVICE AREA */}
        {tab === "pr" && isPr && (
          <section className="mb-10" data-testid="tab-pr">
            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6 sm:p-8">
              {prLoading ? (
                <div className="p-6 text-center text-[#94A3B8] text-sm">Caricamento…</div>
              ) : !prStats ? (
                <div className="p-8 text-center">
                  <Megaphone className="w-10 h-10 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.2} />
                  <p className="text-[#E5E4E2] text-sm">Dati non disponibili al momento.</p>
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div>
                      <span className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Il tuo codice PR</span>
                      {prStats.code ? (
                        <button type="button"
                                onClick={() => { navigator.clipboard?.writeText(prStats.code); setPrCopied(true); setTimeout(() => setPrCopied(false), 1500); }}
                                data-testid="pr-self-code-badge"
                                className={`mt-1 flex items-center gap-2 px-3 py-1.5 rounded-full border text-[13px] font-mono tracking-wider ${
                                  prStats.code_active ? "border-emerald-400/40 text-emerald-200 bg-emerald-500/10" : "border-white/10 text-[#94A3B8] bg-white/5"
                                }`}>
                          {prCopied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {prStats.code}
                        </button>
                      ) : (
                        <p className="mt-1 text-[#94A3B8] text-sm">Nessun codice assegnato</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
                    <StatCard label="Clienti portati" value={prStats.customers_count} icon={Package} testId="pr-self-customers" />
                    <StatCard label="Fatturato totale" value={`€${Number(prStats.total_revenue || 0).toFixed(2)}`} icon={Coins} testId="pr-self-revenue" />
                    <StatCard label="Ultimi 30gg" value={`€${Number(prStats.revenue_30d || 0).toFixed(2)}`} icon={Truck} testId="pr-self-revenue-30d" />
                    <StatCard label="Premi maturati" value={prStats.premi_maturati || 0} icon={Award} testId="pr-self-rewards" />
                  </div>

                  <div data-testid="pr-self-reward-progress">
                    <div className="flex items-center justify-between text-[12px] mb-1.5">
                      <span className="text-[#94A3B8] tracking-[0.1em] uppercase text-[10px]">Prossimo premio</span>
                      <span className="text-white font-[Outfit]">€{Number(prStats.progresso || 0).toFixed(2)} / €{Number(prStats.reward_threshold || 10000).toLocaleString("it-IT")}</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-400"
                           style={{ width: `${Math.min(100, Math.round(((prStats.progresso || 0) / (prStats.reward_threshold || 10000)) * 100))}%` }} />
                    </div>
                    <p className="mt-2 text-[11px] text-[#94A3B8]">
                      Ogni €{Number(prStats.reward_threshold || 10000).toLocaleString("it-IT")} di fatturato generato dai tuoi clienti matura un nuovo premio.
                    </p>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* TAB: PROFILE & PRIVACY */}
        {tab === "profile" && (
          <section className="mb-10 space-y-5" data-testid="tab-profile">
            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6">
              <div className="flex items-center gap-2 mb-5">
                <UserCog className="w-4 h-4 text-[#C0C0C0]" strokeWidth={1.5} />
                <h3 className="text-white text-[14px] font-medium tracking-tight">Dati profilo e fatturazione elettronica</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <div className="sm:col-span-2">
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">Nome completo</label>
                  <input value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))}
                         data-testid="profile-name-input"
                         className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">Codice SDI (7 caratteri)</label>
                  <input value={profile.codice_sdi} maxLength={7}
                         onChange={(e) => setProfile((p) => ({ ...p, codice_sdi: e.target.value.toUpperCase() }))}
                         placeholder="Es. M5UXCR1" data-testid="profile-sdi-input"
                         className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm uppercase focus:outline-none focus:border-[#C0C0C0]" />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">PEC per Fatturazione</label>
                  <input value={profile.pec_fatturazione} type="email"
                         onChange={(e) => setProfile((p) => ({ ...p, pec_fatturazione: e.target.value }))}
                         placeholder="nome@pec.it" data-testid="profile-pec-input"
                         className="mt-2 w-full bg-[var(--lv-bg,#060A14)] border border-white/10 rounded-sm px-4 py-3 text-white text-sm focus:outline-none focus:border-[#C0C0C0]" />
                </div>
              </div>
              {profileMsg && (
                <p data-testid="profile-msg" className="mt-4 text-[12px] text-emerald-300">{profileMsg}</p>
              )}
              <button onClick={saveProfile} disabled={profileBusy} data-testid="profile-save-btn"
                      className="mt-5 inline-flex items-center gap-2 bg-[#C0C0C0] text-[#060A14] px-6 py-3 rounded-sm text-[10px] tracking-[0.28em] uppercase font-medium hover:bg-white disabled:opacity-50">
                {profileBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salva profilo
              </button>
            </div>

            <div className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-emerald-300" strokeWidth={1.5} />
                <h3 className="text-white text-[14px] font-medium tracking-tight">I tuoi dati (GDPR)</h3>
              </div>
              <p className="text-[12px] text-[#94A3B8] leading-relaxed max-w-2xl">
                Ai sensi degli artt. 17 e 20 del Reg. UE 2016/679 puoi esportare tutti i dati che
                conserviamo su di te oppure eliminare definitivamente il tuo account.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button onClick={exportData} data-testid="export-data-btn"
                        className="inline-flex items-center gap-2 border border-[#C0C0C0]/40 text-[#E5E4E2] px-5 py-3 rounded-sm text-[10px] tracking-[0.28em] uppercase hover:bg-[#C0C0C0]/10">
                  <Download className="w-3.5 h-3.5" /> Esporta i miei dati
                </button>
                <button onClick={deleteAccount} data-testid="delete-account-btn"
                        className="inline-flex items-center gap-2 border border-red-500/40 text-red-300 px-5 py-3 rounded-sm text-[10px] tracking-[0.28em] uppercase hover:bg-red-500/10">
                  <Trash2 className="w-3.5 h-3.5" /> Elimina il mio account
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Forum shortcut */}
        <section data-testid="account-forum-cta" className="bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6 flex items-center justify-between gap-5">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-md bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5 text-emerald-300" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <h3 className="font-[Outfit] text-white text-lg tracking-tight">Community LEOVERA</h3>
              <p className="text-[12px] text-[#94A3B8]">Discuti con altri atleti, chiedi consigli, condividi i tuoi progressi.</p>
            </div>
          </div>
          <Link to="/forum" data-testid="account-go-forum"
                className="shrink-0 inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-200 px-4 py-2.5 rounded-sm text-[10px] tracking-[0.28em] uppercase hover:bg-emerald-500/10">
            Apri Forum
          </Link>
        </section>
      </main>
    </div>
  );
};

export default Account;
